import { z } from "zod";

export const QueryPartnerProfileSchema = z
  .object({
    id: z.string().optional(),
    pid: z.string().optional()
  })
  .refine((data) => data.id !== undefined || data.pid !== undefined, {
    message: "Either id or pid must be provided"
  });
export type QueryPartnerProfile = z.infer<typeof QueryPartnerProfileSchema>;

const AddressSchema = z.object({
  province: z.string(),
  district: z.string(),
  ward: z.string(),
  streetDetail: z.string()
});

const GuardianSchema = z.object({
  fullName: z.string(),
  phoneNumber: z.string(),
  relationship: z.string()
});

const LanguageProficiencySchema = z.object({
  language: z.string(),
  proficiencyLevel: z.string()
});

const PartnerApplicationSchema = z.object({
  applicationId: z.string(),
  status: z.enum([
    "DEPOSIT_PENDING",
    "SUBMITTED",
    "UNDER_REVIEW",
    "NEEDS_SUPPLEMENT",
    "ACCEPTED",
    "REJECTED",
    "CANCELLED"
  ]),
  submittedAt: z.string().nullable(),
  program: z.object({
    id: z.string(),
    title: z.string(),
    type: z.enum(["DU_HOC", "VIEC_LAM", "XUAT_KHAU", "HOC_NGHE"]),
    destinationCountry: z.string().nullable(),
    location: z.string().nullable(),
    applicationDeadline: z.string().nullable()
  })
});

export const PartnerProfileRespSchema = z.object({
  id: z.string(),
  fullName: z.string(),
  username: z.string(),
  email: z.string(),
  phoneNumber: z.string().nullable(),
  gender: z.enum(["MALE", "FEMALE", "OTHER", "PREFER_NOT_TO_SAY"]),
  dateOfBirth: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  personal: z
    .object({
      nationalId: z.string().nullable(),
      nationalIdIssuedAt: z.string().nullable(),
      nationalIdIssuedBy: z.string().nullable(),
      permanentAddress: AddressSchema.nullable(),
      currentAddress: AddressSchema.nullable(),
      zaloId: z.string().nullable(),
      facebookUrl: z.string().nullable(),
      guardian: GuardianSchema.nullable()
    })
    .nullable(),
  education: z
    .object({
      level: z.string().nullable(),
      schoolName: z.string().nullable(),
      graduationYear: z.number().nullable(),
      academicGrade: z.string().nullable(),
      vocationalCertificates: z.array(z.string()),
      languageProficiencies: z.array(LanguageProficiencySchema),
      computerSkillSummary: z.string().nullable(),
      driverLicenseClasses: z.array(z.string())
    })
    .nullable(),
  applications: z.array(PartnerApplicationSchema),
  partner: z.object({
    id: z.string(),
    pid: z.string().nullable(),
    status: z.enum(["ACTIVE", "REVOKED"]),
    createdAt: z.string(),
    updatedAt: z.string(),
    linkedAt: z.string(),
    lastSyncAt: z.string().nullable()
  })
});
export type PartnerProfileResp = z.infer<typeof PartnerProfileRespSchema>;

export const CreatePartnerReqSchema = z.object({
  name: z.string().min(1),
  status: z.enum(["ACTIVE", "INACTIVE"]).optional().default("ACTIVE")
});
export type CreatePartnerReq = z.infer<typeof CreatePartnerReqSchema>;

export const CreatePartnerRespSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.enum(["ACTIVE", "INACTIVE"]),
  createdAt: z.string(),
  accessToken: z.string(),
  accessTokenExpiresAt: z.string(),
  refreshToken: z.string(),
  refreshTokenExpiresAt: z.string()
});
export type CreatePartnerResp = z.infer<typeof CreatePartnerRespSchema>;

export const PartnerTokenRespSchema = z.object({
  accessToken: z.string(),
  accessTokenExpiresAt: z.string(),
  refreshToken: z.string(),
  refreshTokenExpiresAt: z.string()
});
export type PartnerTokenResp = z.infer<typeof PartnerTokenRespSchema>;

export const VstarPidRespSchema = z.object({
  pid: z.string().nullable()
});
export type VstarPidResp = z.infer<typeof VstarPidRespSchema>;
